/**
 * Invoicing mandatoriness rules, in one place.
 *
 * Two independent settings decide whether a booking carries the invoicing
 * information its account requires:
 *
 * - `invoiceRefUserGroupMandatory` (account level only) — a cost center must be selected.
 * - `invoiceRefMandatory` — an invoice reference must be given. This one exists at BOTH
 *   the account level and on each selectable cost center, and the two are ORed: an
 *   account that does not require a reference can still select a cost center that does.
 *   Dropping either half of that OR is the bug this module exists to prevent (TUL-5236,
 *   TUL-5353); it was previously written out by hand in five places and had already
 *   drifted apart in three of them.
 *
 * `stepAvailable` encodes a timing rule, not an availability one. Invoicing information
 * is only collected during booking when `invoiceRefBeforeHand` /
 * `invoiceRefBeforeHandPrebooking` is set; otherwise it is asked after the call (see
 * `containers/feedback.js`). A field whose wizard step is not in the current path is
 * therefore unreachable, and requiring it would lock the customer out of a booking with
 * no way to satisfy the requirement. It defaults to `true` so callers with no step path
 * of their own (the billing summary) behave exactly as before.
 */

// Trimmed, so a mandatory reference cannot be satisfied with blank input. `CustomerReference`
// only length-checks on change (`isRefFieldValid`), so three spaces reach `invoiceRef` even
// though its own blur handler trims and marks the same value invalid.
// Exported so `utils/usergroupReferenceDefaults.js` decides "the customer left this blank"
// by exactly the same rule that decides "this mandatory reference is unsatisfied".
export const hasText = (value) => value !== null && value !== undefined && String(value).trim().length > 0;

/**
 * A cost center must be selected. `alwaysRequired` covers the billing summary, where a
 * cost center is required before saving regardless of the account flag, so that an
 * interpretation is never confirmed without invoicing information (TUL-4891).
 */
export const isCostCenterRequired = ({
  costCenterMandatory,
  alwaysRequired = false,
  stepAvailable = true,
} = {}) => {
  if (!stepAvailable) {
    return false;
  }
  return Boolean(costCenterMandatory) || Boolean(alwaysRequired);
};

/**
 * An invoice reference must be given, because either the account requires one or the
 * selected cost center does.
 */
export const isInvoiceRefRequired = ({
  accountRequiresInvoiceRef,
  costCenterRequiresInvoiceRef,
  stepAvailable = true,
} = {}) => {
  if (!stepAvailable) {
    return false;
  }
  return Boolean(accountRequiresInvoiceRef) || Boolean(costCenterRequiresInvoiceRef);
};

/**
 * Both rules plus whether they are currently satisfied. `*Missing` drives the "required"
 * indicators on the summary; `isInvoicingComplete` gates submitting.
 */
export const getInvoicingRequirements = ({
  costCenterMandatory,
  costCenterAlwaysRequired = false,
  costCenterStepAvailable = true,
  hasCostCenter,
  accountRequiresInvoiceRef,
  costCenterRequiresInvoiceRef,
  invoiceRefStepAvailable = true,
  invoiceRef,
} = {}) => {
  const costCenterRequired = isCostCenterRequired({
    costCenterMandatory,
    alwaysRequired: costCenterAlwaysRequired,
    stepAvailable: costCenterStepAvailable,
  });
  const invoiceRefRequired = isInvoiceRefRequired({
    accountRequiresInvoiceRef,
    costCenterRequiresInvoiceRef,
    stepAvailable: invoiceRefStepAvailable,
  });

  const costCenterMissing = costCenterRequired && !hasCostCenter;
  const invoiceRefMissing = invoiceRefRequired && !hasText(invoiceRef);

  return {
    costCenterRequired,
    invoiceRefRequired,
    costCenterMissing,
    invoiceRefMissing,
    isInvoicingComplete: !costCenterMissing && !invoiceRefMissing,
  };
};
